import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { clientsApi, formFillingApi } from '../utils/api';
import { useToast } from '../hooks/useToast';

function ClientModal({ client, onClose, onSave }) {
  const toast = useToast();
  const [form, setForm] = useState(client || {
    name: '', email: '',
    pitchSheetId: '', bookingReportSheetId: '',
    clientLink: ''
  });
  const [saving, setSaving] = useState(false);
  const [profileUploading, setProfileUploading] = useState(false);
  const [hasProfile, setHasProfile] = useState(!!client?.formProfile);
  const profileFileRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const extractSheetId = (input) => {
    const match = input.match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : input.trim();
  };
  const setSheet = (k, v) => set(k, extractSheetId(v));

  const handleSave = async () => {
    if (!form.name) { toast('Name is required', 'error'); return; }
    if (!form.email) { toast('Email is required', 'error'); return; }
    setSaving(true);
    try {
      const res = client
        ? await clientsApi.update(client.id, form)
        : await clientsApi.create(form);
      onSave(res.data);
      toast(client ? 'Client updated' : 'Client created', 'success');
    } catch (e) {
      toast(e.response?.data?.error || 'Error saving client', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>{client ? 'Edit Client' : 'New Client'}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="form-group">
          <label>Name *</label>
          <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Sam Kukathas" autoFocus />
        </div>

        <div className="form-group">
          <label>Email *</label>
          <input value={form.email} onChange={e => set('email', e.target.value)} placeholder="client@email.com" type="email" />
        </div>

        <div className="form-group">
          <label>Client Link <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(used in follow-up emails — press sheet, Instagram, website, etc.)</span></label>
          <input value={form.clientLink || ''} onChange={e => set('clientLink', e.target.value)} placeholder="https://..." />
        </div>

        <div className="divider" />
        <div style={{ marginBottom: 12, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Google Sheets</div>
        <div className="form-group">
          <label>Podcast Pitch List</label>
          <input value={form.pitchSheetId} onChange={e => setSheet('pitchSheetId', e.target.value)} placeholder="Paste the full Google Sheets link or Sheet ID" />
        </div>

        <div className="form-group">
          <label>Booking Report</label>
          <input value={form.bookingReportSheetId} onChange={e => setSheet('bookingReportSheetId', e.target.value)} placeholder="Paste the full Google Sheets link or Sheet ID" />
        </div>

        <div className="divider" />
        <div style={{ marginBottom: 12, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Form Filling</div>
        <div className="form-group">
          <label>Form Profile <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(bio, social links, website, speaking topics — used to fill podcast intake forms)</span></label>
          <input ref={profileFileRef} type="file" accept=".txt,.pdf,.docx,.md" style={{ display: 'none' }}
            onChange={async e => {
              const file = e.target.files[0];
              if (!file || !client?.id) return;
              setProfileUploading(true);
              try {
                await formFillingApi.uploadProfile(client.id, file);
                setHasProfile(true);
                toast('Form profile uploaded ✓', 'success');
              } catch { toast('Upload failed', 'error'); }
              finally { setProfileUploading(false); e.target.value = ''; }
            }}
          />
          <div className="flex-center gap-2">
            <button className="btn btn-secondary btn-sm" disabled={profileUploading || !client?.id}
              onClick={() => profileFileRef.current.click()}>
              {profileUploading ? 'Uploading...' : hasProfile ? '↑ Replace Profile' : '↑ Upload Profile'}
            </button>
            {hasProfile && (
              <button className="btn btn-sm" style={{ color: 'var(--red)', background: 'var(--red-soft)', border: '1px solid rgba(239,68,68,0.2)' }}
                onClick={async () => {
                  if (!client?.id) return;
                  await formFillingApi.deleteProfile(client.id).catch(() => {});
                  setHasProfile(false);
                  toast('Profile removed', 'success');
                }}>
                Remove
              </button>
            )}
            {hasProfile && <span style={{ fontSize: 12, color: 'var(--green)' }}>✓ Profile uploaded</span>}
            {!hasProfile && !client?.id && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Save client first, then upload</span>}
          </div>
        </div>

        <div className="divider" />

        <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : (client ? 'Save Changes' : 'Add Client')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Clients() {
  const toast = useToast();
  const [clients, setClients] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    clientsApi.getAll().then(async r => {
      const c = r.data;
      setClients(c);
      const active = c.filter(cl => !cl.archived);
      const map = {};
      await Promise.all(active.map(async client => {
        try {
          const s = await clientsApi.dashboardSummary(client.id);
          map[client.id] = s.data;
        } catch (e) {
          map[client.id] = { coveredThisWeek: false, savedCount: 0 };
        }
      }));
      setSummaries(map);
    }).finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    await clientsApi.delete(id);
    setClients(prev => prev.filter(c => c.id !== id));
    toast('Client deleted', 'success');
  };

  const handleArchive = async (client) => {
    const res = await clientsApi.archive(client.id);
    setClients(prev => prev.map(c => c.id === client.id ? res.data : c));
    toast(`${client.name} archived`, 'success');
  };

  const handleUnarchive = async (client) => {
    const res = await clientsApi.unarchive(client.id);
    setClients(prev => prev.map(c => c.id === client.id ? res.data : c));
    toast(`${client.name} restored`, 'success');
  };

  const handleSave = (saved) => {
    setClients(prev => {
      const idx = prev.findIndex(c => c.id === saved.id);
      if (idx > -1) { const next = [...prev]; next[idx] = saved; return next; }
      return [...prev, saved];
    });
    setModal(null);
  };

  const active = clients.filter(c => !c.archived);
  const archived = clients.filter(c => c.archived);

  return (
    <div className="page-content">
      <div className="page-header flex-between">
        <div>
          <h1>Clients</h1>
          <p>{active.length} active client{active.length !== 1 ? 's' : ''}{archived.length > 0 ? ` · ${archived.length} archived` : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}>+ New Client</button>
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading...</p></div>
      ) : active.length === 0 && archived.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">👤</div>
          <h3>No clients yet</h3>
          <p>Add your first client to get started</p>
          <button className="btn btn-primary mt-3" onClick={() => setModal('new')}>Add Client</button>
        </div>
      ) : (
        <>
          {/* Active clients */}
          {active.length === 0 ? (
            <div className="empty-state" style={{ marginBottom: 24 }}>
              <p>No active clients. Add one above or restore an archived client below.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {active.map(client => {
                const s = summaries[client.id] || {};
                const covered = s.coveredThisWeek;
                return (
                  <Link key={client.id} to={`/clients/${client.id}`} style={{ textDecoration: 'none' }}>
                    <div className="card card-hover" style={{
                      display: 'flex', alignItems: 'center', gap: 16,
                      borderLeft: `3px solid ${covered ? 'var(--green)' : 'var(--amber)'}`
                    }}>
                      <div style={{
                        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
                        background: covered ? 'var(--green-soft)' : 'var(--amber-soft)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 17,
                        color: covered ? 'var(--green)' : 'var(--amber)'
                      }}>
                        {client.name.charAt(0).toUpperCase()}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)' }}>{client.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{client.email}</div>
                      </div>

                      <div style={{ display: 'flex', gap: 20, fontSize: 12, color: 'var(--text-secondary)' }}>
                        {s.savedCount > 0 && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'Syne, sans-serif', color: 'var(--green)' }}>{s.savedCount}</div>
                            <div style={{ color: 'var(--text-muted)' }}>saved</div>
                          </div>
                        )}
                        {s.pitched > 0 && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'Syne, sans-serif', color: 'var(--blue)' }}>{s.pitched}</div>
                            <div style={{ color: 'var(--text-muted)' }}>pitched</div>
                          </div>
                        )}
                        {s.followedUp > 0 && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'Syne, sans-serif', color: 'var(--accent)' }}>{s.followedUp}</div>
                            <div style={{ color: 'var(--text-muted)' }}>follow-ups</div>
                          </div>
                        )}
                        {s.booked > 0 && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'Syne, sans-serif', color: 'var(--green)' }}>{s.booked}</div>
                            <div style={{ color: 'var(--text-muted)' }}>booked</div>
                          </div>
                        )}
                      </div>

                      <span className={`badge ${covered ? 'badge-green' : 'badge-amber'}`}>
                        {covered ? '✓ Covered' : '⚠ Needs Pitch'}
                      </span>

                      <div className="flex-center gap-2" onClick={e => e.preventDefault()}>
                        <button className="btn btn-ghost btn-sm" onClick={e => { e.preventDefault(); setModal(client); }}>Edit</button>
                        <button className="btn btn-ghost btn-sm" onClick={e => { e.preventDefault(); handleArchive(client); }}>Archive</button>
                        <button className="btn btn-danger btn-sm" onClick={e => { e.preventDefault(); handleDelete(client.id, client.name); }}>Delete</button>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Archived clients — collapsible */}
          {archived.length > 0 && (
            <div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowArchived(v => !v)}
                style={{ marginBottom: 12, color: 'var(--text-muted)', fontSize: 12 }}
              >
                {showArchived ? '▾' : '▸'} Archived ({archived.length})
              </button>

              {showArchived && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {archived.map(client => (
                    <div key={client.id} className="card" style={{
                      display: 'flex', alignItems: 'center', gap: 16,
                      borderLeft: '3px solid var(--border)', opacity: 0.7
                    }}>
                      <div style={{
                        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
                        background: 'var(--bg-elevated)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 17,
                        color: 'var(--text-muted)'
                      }}>
                        {client.name.charAt(0).toUpperCase()}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-secondary)' }}>{client.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{client.email}</div>
                      </div>

                      <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Archived</span>

                      <div className="flex-center gap-2">
                        <button className="btn btn-ghost btn-sm" onClick={() => handleUnarchive(client)}>Restore</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(client.id, client.name)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {modal && (
        <ClientModal
          client={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
